/** Secondary asset dialogs: certificates, install/remove component, record position. */
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { useAuth } from "@/app/auth";
import { Attachments } from "@/features/documents/Attachments";
import { DocumentFileLink } from "@/features/entities/DocumentFileLink";
import {
  type Asset,
  type Certificate,
  useCreateCertificate,
  useInstallAsset,
  useRecordPosition,
  useRemoveAsset,
  useUpdateCertificate,
  useRevokeCertificate,
} from "@/features/assets/queries";
import { FormDialog } from "@/shared/components/form-dialog";
import { AssetSelect, LocationPicker, PartyPicker } from "@/shared/components/pickers";
import { isoOrNull, nullable, numberOrNull, useForm } from "@/shared/lib/form";
import { Button } from "@/shared/ui/button";
import { Field, Input, Select, Textarea } from "@/shared/ui/form";
import { Dialog } from "@/shared/ui/primitives";

/** Common certificate kinds; custom kinds remain supported. */
export const CERTIFICATE_KINDS = [
  "registration",
  "safety",
  "class",
  "insurance",
  "radio",
  "load_line",
  "tonnage",
  "other",
] as const;

interface CertificateValues extends Record<string, unknown> {
  kind: string;
  name: string;
  number: string;
  issuer_id: string;
  issued_on: string;
  expires_on: string;
  renewal_lead_days: string;
  notes: string;
  document_id: string;
}

const certificateSchema = z.object({
  kind: z.string().trim().min(1, "Required"),
  name: z.string().trim().min(1, "Required"),
});

/** Add or edit a certificate. */
export function CertificateDialog({
  asset,
  certificate,
  open,
  onOpenChange,
}: {
  asset: Asset;
  certificate?: Certificate;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}): ReactElement {
  const { t } = useTranslation();
  const { can } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const create = useCreateCertificate(asset.id);
  const update = useUpdateCertificate(asset.id);
  const initial = useMemo<CertificateValues>(
    () => ({
      kind: certificate?.kind ?? "registration",
      name: certificate?.name ?? "",
      number: certificate?.number ?? "",
      issuer_id: certificate?.issuer_id ?? "",
      issued_on: certificate?.issued_on ?? "",
      expires_on: certificate?.expires_on ?? "",
      renewal_lead_days: String(certificate?.renewal_lead_days ?? 30),
      notes: certificate?.notes ?? "",
      document_id: certificate?.document_id ?? "",
    }),
    [certificate],
  );
  const form = useForm<CertificateValues>({ initial, schema: certificateSchema, idPrefix: "cert" });
  const [customKind, setCustomKind] = useState(
    () => !CERTIFICATE_KINDS.some((kind) => kind === initial.kind),
  );

  const submit = async (): Promise<void> => {
    const ok = await form.submit(async (v) => {
      const body = {
        kind: v.kind.trim(),
        name: v.name.trim(),
        number: v.number,
        issuer_id: nullable(v.issuer_id),
        issued_on: nullable(v.issued_on),
        expires_on: nullable(v.expires_on),
        renewal_lead_days: numberOrNull(v.renewal_lead_days) ?? 30,
        document_id: nullable(v.document_id),
        notes: v.notes,
      };
      if (certificate) {
        await update.mutateAsync({ id: certificate.id, body });
      } else {
        await create.mutateAsync(body);
      }
    });
    if (ok) {
      onOpenChange(false);
    }
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={certificate ? t("assets.editCertificate") : t("assets.addCertificate")}
      onSubmit={submit}
      submitting={form.submitting || uploading}
      dirty={JSON.stringify(form.values) !== JSON.stringify(initial) || uploading}
      error={form.errors._}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field
          id="cert-kind-choice"
          label={t("app.type")}
          error={customKind ? undefined : form.errors.kind}
        >
          <Select
            id="cert-kind-choice"
            value={customKind ? "" : form.values.kind}
            onChange={(event) => {
              setCustomKind(event.target.value === "");
              form.set("kind", event.target.value);
            }}
            aria-invalid={!customKind && form.errors.kind ? true : undefined}
            disabled={form.submitting}
          >
            {CERTIFICATE_KINDS.map((kind) => (
              <option key={kind} value={kind}>
                {t(`assets.certificateKinds.${kind}`)}
              </option>
            ))}
            <option value="">{t("assets.customCertificateType")}</option>
          </Select>
        </Field>
        {customKind ? (
          <Field id="cert-kind" label={t("assets.customCertificateType")} error={form.errors.kind}>
            <Input {...form.field("kind")} required disabled={form.submitting} />
          </Field>
        ) : null}
        <Field id="cert-name" label={t("app.name")} error={form.errors.name}>
          <Input {...form.field("name")} required />
        </Field>
        <Field id="cert-number" label={t("assets.certificateNumber")}>
          <Input {...form.field("number")} />
        </Field>
        <Field id="cert-issuer_id" label={t("assets.issuer")}>
          <PartyPicker
            id="cert-issuer_id"
            value={form.values.issuer_id}
            onChange={(id) => form.set("issuer_id", id ?? "")}
          />
        </Field>
        <Field id="cert-issued_on" label={t("assets.issuedOn")} error={form.errors.issued_on}>
          <Input type="date" {...form.field("issued_on")} />
        </Field>
        <Field id="cert-expires_on" label={t("assets.expires")} error={form.errors.expires_on}>
          <Input type="date" {...form.field("expires_on")} />
        </Field>
        <Field
          id="cert-renewal_lead_days"
          label={t("assets.renewalLead")}
          error={form.errors.renewal_lead_days}
        >
          <Input type="number" min="0" max="730" {...form.field("renewal_lead_days")} />
        </Field>
      </div>
      <Field id="cert-notes" label={t("app.notes")}>
        <Textarea {...form.field("notes")} />
      </Field>
      <section className="space-y-2" aria-label={t("fileEvidence.certificateFile")}>
        <h3 className="text-sm font-medium">{t("fileEvidence.certificateFile")}</h3>
        {form.values.document_id ? (
          <div className="flex flex-wrap items-center gap-2">
            <DocumentFileLink
              id={form.values.document_id}
              versionId={
                form.values.document_id === certificate?.document_id
                  ? certificate.document_version_id
                  : undefined
              }
              label={t("fileEvidence.openSelected")}
            />
            {can("assets.certificate.manage") && can("documents.document.upload") ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setReplacing(true)}
                >
                  {t("feedbackAssets.replaceFile")}
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={() => setRemoveOpen(true)}>
                  {t("feedbackAssets.removeFile")}
                </Button>
              </>
            ) : null}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{t("fileEvidence.noFile")}</p>
        )}
        {can("assets.certificate.manage") &&
        can("documents.document.upload") &&
        (!form.values.document_id || replacing) ? (
          <Attachments
            entityType="asset"
            entityId={asset.id}
            compact
            allowAlreadyLinked
            onSelected={(id) => {
              form.set("document_id", id);
              setReplacing(false);
            }}
            onBusyChange={setUploading}
          />
        ) : null}
        <Dialog
          open={removeOpen}
          onOpenChange={setRemoveOpen}
          title={t("feedbackAssets.removeFile")}
        >
          <p className="mb-4 text-sm">{t("feedbackAssets.removeFileConfirm")}</p>
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setRemoveOpen(false)}>
              {t("app.cancel")}
            </Button>
            <Button
              type="button"
              onClick={() => {
                form.set("document_id", "");
                setRemoveOpen(false);
                setReplacing(false);
              }}
            >
              {t("feedbackAssets.removeFile")}
            </Button>
          </div>
        </Dialog>
      </section>
    </FormDialog>
  );
}

/** Revoke without deleting certificate metadata or its pinned file history. */
export function RevokeCertificateDialog({
  certificate,
  onClose,
}: {
  certificate: Certificate;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const { can } = useAuth();
  const mutation = useRevokeCertificate(certificate.asset_id);
  const form = useForm<{ reason: string }>({
    initial: { reason: "" },
    schema: z.object({ reason: z.string().trim().min(1, t("feedbackAssets.reasonRequired")) }),
    idPrefix: "revoke-certificate",
  });
  return (
    <FormDialog
      open
      onOpenChange={onClose}
      title={t("feedbackAssets.revokeCertificate")}
      submitLabel={t("feedbackAssets.revokeCertificate")}
      submitting={form.submitting}
      error={form.errors._}
      onSubmit={async () => {
        if (!can("assets.certificate.manage")) return;
        if (
          await form.submit(async (values) => {
            await mutation.mutateAsync({ id: certificate.id, reason: values.reason.trim() });
          })
        )
          onClose();
      }}
    >
      <p className="text-sm">{t("feedbackAssets.revokeConfirm", { name: certificate.name })}</p>
      <Field id="revoke-certificate-reason" label={t("app.reason")} error={form.errors.reason}>
        <Textarea {...form.field("reason")} required />
      </Field>
    </FormDialog>
  );
}

interface InstallValues extends Record<string, unknown> {
  parent_id: string;
  position: string;
  installed_at: string;
  meter_value: string;
}

/** Install an uninstalled component onto a parent asset. */
export function InstallDialog({
  asset,
  open,
  onOpenChange,
}: {
  asset: Asset;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}): ReactElement {
  const { t } = useTranslation();
  const install = useInstallAsset(asset.id);
  const form = useForm<InstallValues>({
    initial: { parent_id: "", position: asset.position, installed_at: "", meter_value: "" },
    schema: z.object({ parent_id: z.string().min(1, "Required") }),
    idPrefix: "install",
  });
  const submit = async (): Promise<void> => {
    const ok = await form.submit((v) =>
      install.mutateAsync({
        parent_id: v.parent_id,
        position: v.position,
        installed_at: isoOrNull(v.installed_at),
        meter_value: numberOrNull(v.meter_value),
        workorder_id: null,
      }),
    );
    if (ok) {
      onOpenChange(false);
    }
  };
  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("assets.install")}
      onSubmit={submit}
      submitting={form.submitting}
      error={form.errors._}
      submitLabel={t("assets.install")}
    >
      <Field id="install-parent_id" label={t("assets.parent")} error={form.errors.parent_id}>
        <AssetSelect
          id="install-parent_id"
          value={form.values.parent_id}
          onChange={(id) => form.set("parent_id", id ?? "")}
          excludeId={asset.id}
          installAssetId={asset.id}
          required
        />
      </Field>
      <Field id="install-position" label={t("assets.positionOnParent")}>
        <Input {...form.field("position")} />
      </Field>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field id="install-installed_at" label={t("assets.installedAt")}>
          <Input type="datetime-local" {...form.field("installed_at")} />
        </Field>
        <Field id="install-meter_value" label={t("assets.meterAtInstall")}>
          <Input type="number" step="any" {...form.field("meter_value")} />
        </Field>
      </div>
    </FormDialog>
  );
}

interface RemoveValues extends Record<string, unknown> {
  reason: string;
  removed_at: string;
  meter_value: string;
  to_location_id: string;
}

/** Remove a component from its parent (optionally into a stock location). */
export function RemoveDialog({
  asset,
  open,
  onOpenChange,
}: {
  asset: Asset;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}): ReactElement {
  const { t } = useTranslation();
  const remove = useRemoveAsset(asset.id);
  const form = useForm<RemoveValues>({
    initial: { reason: "", removed_at: "", meter_value: "", to_location_id: "" },
    idPrefix: "remove",
  });
  const submit = async (): Promise<void> => {
    const ok = await form.submit((v) =>
      remove.mutateAsync({
        reason: v.reason,
        removed_at: isoOrNull(v.removed_at),
        meter_value: numberOrNull(v.meter_value),
        to_location_id: nullable(v.to_location_id),
        workorder_id: null,
      }),
    );
    if (ok) {
      onOpenChange(false);
    }
  };
  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("assets.remove")}
      onSubmit={submit}
      submitting={form.submitting}
      error={form.errors._}
      submitLabel={t("assets.remove")}
    >
      <Field id="remove-reason" label={t("app.reason")}>
        <Textarea {...form.field("reason")} />
      </Field>
      <Field id="remove-to_location_id" label={t("assets.moveToLocation")}>
        <LocationPicker
          id="remove-to_location_id"
          siteId={asset.site?.id ?? null}
          value={form.values.to_location_id}
          onChange={(id) => form.set("to_location_id", id ?? "")}
        />
      </Field>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field id="remove-removed_at" label={t("assets.removedAt")}>
          <Input type="datetime-local" {...form.field("removed_at")} />
        </Field>
        <Field id="remove-meter_value" label={t("assets.meterAtRemoval")}>
          <Input type="number" step="any" {...form.field("meter_value")} />
        </Field>
      </div>
    </FormDialog>
  );
}

interface PositionValues extends Record<string, unknown> {
  lat: string;
  lon: string;
  recorded_at: string;
  speed_knots: string;
  heading_deg: string;
}

/** Record a manual position, with a "use my location" shortcut. */
export function PositionDialog({
  asset,
  open,
  onOpenChange,
}: {
  asset: Asset;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}): ReactElement {
  const { t } = useTranslation();
  const record = useRecordPosition(asset.id);
  const form = useForm<PositionValues>({
    initial: {
      lat: asset.current_position ? String(asset.current_position.lat) : "",
      lon: asset.current_position ? String(asset.current_position.lon) : "",
      recorded_at: "",
      speed_knots: "",
      heading_deg: "",
    },
    schema: z.object({
      lat: z.coerce.number().min(-90).max(90),
      lon: z.coerce.number().min(-180).max(180),
    }),
    idPrefix: "pos",
  });
  const locate = (): void => {
    globalThis.navigator?.geolocation?.getCurrentPosition((p) =>
      form.patch({ lat: p.coords.latitude.toFixed(6), lon: p.coords.longitude.toFixed(6) }),
    );
  };
  const submit = async (): Promise<void> => {
    const ok = await form.submit((v) =>
      record.mutateAsync({
        lat: Number(v.lat),
        lon: Number(v.lon),
        recorded_at: isoOrNull(v.recorded_at),
        source: "manual",
        speed_knots: numberOrNull(v.speed_knots),
        heading_deg: numberOrNull(v.heading_deg),
        accuracy_m: null,
        source_ref: "",
      }),
    );
    if (ok) {
      onOpenChange(false);
    }
  };
  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("assets.recordPosition")}
      onSubmit={submit}
      submitting={form.submitting}
      error={form.errors._}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field id="pos-lat" label={t("assets.latitude")} error={form.errors.lat}>
          <Input type="number" step="any" {...form.field("lat")} required />
        </Field>
        <Field id="pos-lon" label={t("assets.longitude")} error={form.errors.lon}>
          <Input type="number" step="any" {...form.field("lon")} required />
        </Field>
        <Field id="pos-speed_knots" label={t("assets.speedKnots")}>
          <Input type="number" step="any" min="0" {...form.field("speed_knots")} />
        </Field>
        <Field id="pos-heading_deg" label={t("assets.heading")}>
          <Input type="number" min="0" max="360" {...form.field("heading_deg")} />
        </Field>
        <Field id="pos-recorded_at" label={t("assets.recordedAt")}>
          <Input type="datetime-local" {...form.field("recorded_at")} />
        </Field>
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={locate}
        disabled={!globalThis.navigator?.geolocation}
      >
        {t("assets.useMyLocation")}
      </Button>
    </FormDialog>
  );
}
